<template>
    <div 
        class="automation-department-permission-row" 
        :class="{ 
            'row-odd': isOdd, 
            'inactive': !isActive,
            'blur-row': !isActive
        }"
    >
        <div class="target-cell">
            <div class="target-info">
                <i class="material-icons">group</i>
                <span class="target-name">{{ departmentName }}</span>
            </div>
            <SupsisButton 
                v-if="!isActive"
                ghost 
                primary
                icon="add" 
                small 
                @click="$emit('activate', department._id)"
                v-tippy="{ content: $t('automation.permissions.grantPermission') }"
            />
            <SupsisButton 
                v-else
                ghost 
                danger 
                icon="remove_circle" 
                small 
                @click="$emit('deactivate', department._id)"
                v-tippy="{ content: $t('automation.permissions.revokePermission') }"
            />
        </div>

        <div 
            v-for="group in scopeGroups" 
            :key="group.id" 
            class="scope-group-cell"
        >
            <div class="scope-toggles">
                <div 
                    v-for="scope in group.scopes" 
                    :key="scope"
                    class="scope-toggle-cell"
                    @click="isActive && toggleScope(scope)"
                >
                    <SupsisButton 
                        :class="{ 'scope-active': hasScope(scope) }"
                        :disabled="!isActive"
                    >
                        <i class="material-icons" v-if="hasScope(scope)">done</i>
                        <div 
                            v-else 
                            class="disabled-item"
                            @mouseenter="isActive && hoverEffect($event, true)"
                            @mouseleave="isActive && hoverEffect($event, false)"
                        >
                            <i class="material-icons">done</i>
                            <i class="material-icons">circle</i>
                        </div>
                    </SupsisButton>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";

export default Vue.extend({
    name: "AutomationDepartmentPermissionRow",
    components: {
        SupsisButton,
    },
    props: {
        department: {
            type: Object,
            required: true,
        },
        target: {
            type: Object,
            default: null,
        },
        targetIndex: {
            type: Number,
            default: -1,
        },
        scopeGroups: {
            type: Array,
            required: true,
        },
        users: {
            type: Array,
            required: true,
        },
        permissionTargets: {
            type: Array,
            required: true,
        },
        isActive: {
            type: Boolean,
            default: false,
        },
        isOdd: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        departmentName: function() {
            return this.department.name || this.$t("automation.permissions.unknownDepartment");
        },
    },
    methods: {
        hasScope: function(scope) {
            if (!this.isActive || !this.target) return false;
            return (this.target.scopes || []).includes(scope);
        },
        toggleScope: function(scope) {
            if (!this.isActive) return;
            this.$emit("toggle-scope", { targetIndex: this.targetIndex, scope: scope });
        },
        hoverEffect: function(e, hover) {
            e.target.classList[hover ? "add" : "remove"]("active");
        },
    },
});
</script>

<style scoped lang="scss">
.automation-department-permission-row {
    display: flex;
    border-bottom: 1px solid var(--color-border-default);
    min-height: 50px;
    transition: all 0.3s ease;

    &.row-odd {
        background: var(--color-bg-neutral-default);
    }

    &.inactive {
        opacity: 0.5;
        
        &.blur-row {
            filter: blur(0.5px);
        }
    }

    &:hover {
        background: var(--color-bg-neutral-hover);
        
        &.inactive {
            opacity: 0.8;
            filter: blur(0);
        }
    }

    .target-cell {
        width: 250px;
        min-width: 250px;
        flex-shrink: 0;
        padding: 8px 12px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        border-right: 1px solid var(--color-border-default);

        .target-info {
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;
            min-width: 0;

            .material-icons {
                font-size: 18px;
                color: var(--color-icon-default);
                flex-shrink: 0;
            }

            .target-name {
                font-weight: 500;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }
        }
    }

    .scope-group-cell {
        flex: 1;
        display: flex;
        border-right: 1px solid var(--color-border-default);

        &:last-child {
            border-right: none;
        }

        .scope-toggles {
            display: flex;
            width: 100%;

            .scope-toggle-cell {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                border-right: 1px solid var(--color-border-subdued);
                cursor: pointer;
                padding: 0;

                &:last-child {
                    border-right: none;
                }

                &:hover {
                    background: var(--color-bg-primary-subdued-hover);
                }

                .supsis-button {
                    width: 100%;
                    height: 100%;
                    min-height: 50px;
                    border: none;
                    background: transparent;
                    padding: 0;

                    &.scope-active {
                        .material-icons {
                            color: var(--color-icon-success);
                        }
                    }

                    .disabled-item {
                        :first-child {
                            display: none;
                        }

                        &.active {
                            :first-child {
                                display: block;
                            }
                            :last-child {
                                display: none;
                            }
                        }
                    }

                    .material-icons {
                        font-size: 20px;
                    }
                }
            }
        }
    }
}
</style>
